import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ApiClientConstant, Table } from "@cureskin/api-client";
import { ConnectionService } from "@services/connection-service";
import { DoctorProfile } from '@cureskin/api-client-rn';
import { EventLoggerService } from "@services/event-logger-service";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import {
  Regimen,
  RegimenExpiryOutput,
} from '@cureskin/api-client/src/server-api/api';
import { AppConfig } from "client/app/app.config";
import { ConsultationSessionService } from '@services/consultation-session-service';
export type RegimenExpiryBannerData = RegimenExpiryOutput & {
  showCoupon: boolean;
  couponDetails: {
    amount: number;
    couponCode: string;
  } | null;
};

export interface RegimenExpiryBannerResult {
  showBanner: boolean;
  showProductBottomSheet: boolean;
  bannerData: RegimenExpiryBannerData;
  userDrImage?: string;
}

export interface ExperimentVariant {
  bannerThresholdInDays: number;
  showRegimenExpiryAfter: number;
  expireRegimenAfter: number
  minRegimenProductsOrdered: number;
  couponCode: string;
}

export interface RegimenExpiredBannerParams {
  regimens: Array<any>;
  totalAmountSpent: number;
  isPaidUser: boolean;
  consultationSession?: Table.ConsultationSession;
  skipHide?: boolean;
}
@Injectable({
  providedIn: 'root'
})

export class RegimenExpiryService {
  private couponCodeDetails: { couponCode: string, isCouponApplied: boolean } = {
    couponCode: null,
    isCouponApplied: false,
  };

  private productData: { productId: string, quantity: number }[] = [];

  hideRegimenExpiredBanner: boolean = false;

  private isAnyReacquisitionBannerShown: boolean = false;

  constructor(
    private readonly conn: ConnectionService,
    private readonly eventLogger: EventLoggerService,
    private readonly router: Router,
    private readonly bottomSheet: MatBottomSheet,
    private readonly appConfig: AppConfig,
    private readonly consultationSessionService: ConsultationSessionService,
  ) {}

  getCouponCodeDetails(): { couponCode: string, isCouponApplied: boolean } {
    return this.couponCodeDetails;
  }

  setCouponCodeDetails(couponCodeDetails: { couponCode: string, isCouponApplied: boolean }): void {
    this.couponCodeDetails = couponCodeDetails;
  }

  getSelectedProducts(): { productId: string, quantity: number }[] {
    return this.productData;
  }

  setSelectedProducts(productData: { productId: string, quantity: number }[]): void {
    this.productData = [...productData];
  }

  updateProductQuantity(productId: string, quantity: number): void {
    const existingIndex = this.productData.findIndex(item => item.productId === productId);
    if (existingIndex !== -1) {
      this.productData[existingIndex].quantity = quantity;
    } else {
      this.productData.push({ productId, quantity });
    }
  }

  async handleRegimenExpiryBannerClick(regimenExpiryBannerData: RegimenExpiryBannerData, user: Table.User, userDrImage: string, bottomSheetComponent: any): Promise<void> {
    if (regimenExpiryBannerData?.redirectToFollowUp) {
      await this.handleFollowupRedirect(regimenExpiryBannerData, user?.id);
      return;
    }
    if (!regimenExpiryBannerData?.redirectToFollowUp && regimenExpiryBannerData?.products?.length < 1) {
      this.trackNoProductsEvent(user?.id, regimenExpiryBannerData);
      return;
    }

    const processedProducts = this.getProcessedProducts(regimenExpiryBannerData);
    this.openRegimenExpiryBottomSheet(processedProducts, userDrImage, regimenExpiryBannerData, bottomSheetComponent);
  }

  isRegimenExpiryDateInThreshold(user: Table.User, expiryInDaysThreshold: number = 30): boolean {
    const regimenExpiryDate = user?.get('regimenExpiryDate');
    const currentDate = new Date();
    //Hiding the banner if the date is not present
    if (!regimenExpiryDate) {
      return false;
    }
    const expiryDate = regimenExpiryDate instanceof Date ? regimenExpiryDate : new Date(regimenExpiryDate);
    if (isNaN(expiryDate.getTime())) {
      return true;
    }
    const timeDifference = expiryDate.getTime() - currentDate.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    return daysDifference <= (expiryInDaysThreshold + 1);
  }

  async shouldShowRegimenExpiryBanner(params: { user: Table.User, regimenExpiryExperiment: any, allocatedDoctor?: DoctorProfile, defaultDoctorImage: string}): Promise<RegimenExpiryBannerResult> {
    const result: RegimenExpiryBannerResult = {
      showBanner: false,
      showProductBottomSheet: false,
      bannerData: null,
      userDrImage: params?.defaultDoctorImage,
    };

    const { user, regimenExpiryExperiment, allocatedDoctor, defaultDoctorImage } = params;
    if (!regimenExpiryExperiment?.enable) {
      return result;
    }
    const experimentVariant: ExperimentVariant = regimenExpiryExperiment?.variant;
    const isEligibleForExpiryBanner = this.isRegimenExpiryDateInThreshold(user, experimentVariant?.bannerThresholdInDays);

    if (!isEligibleForExpiryBanner) {
     return result;
    }

    // Fetch banner data from API
    const regimenExpiryBannerData: RegimenExpiryOutput = await this.conn.shouldShowRegimenExpiryBanner();
    const showBanner = regimenExpiryBannerData?.showBanner;
    if (!showBanner) {
      return result;
    }

    result.showBanner = true;
    result.bannerData = {
      ...regimenExpiryBannerData,
      showCoupon: false,
      couponDetails: null
    };

    // Check if product bottom sheet banner should be shown
    const showProductBottomSheet = showBanner && !regimenExpiryBannerData?.redirectToFollowUp;
    result.showProductBottomSheet = showProductBottomSheet;

    if (!showProductBottomSheet) {
     return result;
    }

    result.userDrImage = this.getDoctorImage(allocatedDoctor, defaultDoctorImage);

    await this.fetchAndSetCouponDetails(experimentVariant, result.bannerData);

    return result;
  }

  private getDoctorImage(allocatedDoctor: DoctorProfile, defaultDoctorImage: string): string {
    return allocatedDoctor?.doctorDisplayImage
      || allocatedDoctor?.DoctorImageThumbnail
      || defaultDoctorImage;
  }

  private async fetchAndSetCouponDetails(regimenExpiryVariant: ExperimentVariant, bannerData: RegimenExpiryBannerData): Promise<void> {
    try {
      const coupon = await this.conn.fetchCoupon(regimenExpiryVariant?.couponCode);
      bannerData.couponDetails = {
        couponCode: coupon.code,
        amount: coupon.amount
      };
      bannerData.showCoupon = true;
    } catch (error) {
      bannerData.showCoupon = false;
      bannerData.couponDetails = null;
    }
  }

  private getProcessedProducts(regimenExpiryBannerData: RegimenExpiryBannerData): RegimenExpiryBannerData['products'] {
    const filteredProducts = regimenExpiryBannerData?.products?.filter((product: any) => !product?.onHold && product?.inventoryStatus !== ApiClientConstant.Catalog.InventoryStatus.DISCONTINUED);
    const categoryPriority: { [key: string]: number } = {
      FACE: 0,
      BODY: 1,
      HAIR: 2
    };
    filteredProducts?.sort((a: any, b: any) => {
      const aPriority = categoryPriority[a?.category] ?? 99;
      const bPriority = categoryPriority[b?.category] ?? 99;
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      return Number(a?.isPurchasedInLast30Days) - Number(b?.isPurchasedInLast30Days);
    });
    return filteredProducts;
  }

  private openRegimenExpiryBottomSheet(processedProducts: RegimenExpiryBannerData['products'], userDrImage: string, regimenExpiryBannerData: RegimenExpiryBannerData, bottomSheetComponent: any): void {
    const reArrangedProducts = processedProducts;

    this.bottomSheet.open(bottomSheetComponent, {
      data: {
        allocatedDoctorImage: userDrImage,
        products: reArrangedProducts,
        showCoupon: regimenExpiryBannerData?.showCoupon,
        couponDetails: {
          amount: regimenExpiryBannerData?.couponDetails?.amount,
          couponCode: regimenExpiryBannerData?.couponDetails?.couponCode
        }
      }
    });
  }

  isRegimenExpiredForPaidUser(isPaidUser: boolean, regimens: Regimen[]): boolean {
    return isPaidUser && !regimens.some((regimen) => regimen.active);
  }

  shouldShowRegimenExpiredBanner(params: RegimenExpiredBannerParams): boolean {
    const { regimens, totalAmountSpent, isPaidUser, consultationSession, skipHide } = params;
    if (!isPaidUser) return false;
    if (regimens.length > 0) return false;
    if (totalAmountSpent && totalAmountSpent > this.appConfig.Shared.User.MinimumAmountSpentFromReAcq && this.isAnyReacquisitionBannerShown) return false;
    if (!skipHide && this.hideRegimenExpiredBanner) return false;
    if (consultationSession?.get('PrivateMainConcern')) return false;
    return true;
  }

  disableRegimenExpiredBanner(): void {
    this.hideRegimenExpiredBanner = true;
  }

  setReAcquisitionBannerShown(value: boolean): void {
    this.isAnyReacquisitionBannerShown = value;
  }

  getRegimenClass(products: any[]): string {
    if (!products || products.length === 0) {
      return 'FACE';
    }

    const categories = products.map(product => product?.category).filter(Boolean);
    const uniqueCategories = [...new Set(categories)];

    if (uniqueCategories.includes('FACE') && uniqueCategories.includes('HAIR')) {
      return 'BOTH';
    } else if (uniqueCategories.includes('HAIR')) {
      return 'HAIR';
    } else {
      return 'FACE';
    }
  }

  async validateAndNavigateToConsultation(user: Table.User) {
    const consultations = await this.consultationSessionService.getConsultations(user);
    if (consultations?.length === 0) {
      this.router.navigate(['/re-acquisition/concern-selection'], {
        queryParams: { isRebranding: true, isSpentLessThanThreshold: true }
      });
      return;
    }
    const incompleteFaceConsultation = this.consultationSessionService.findIncompleteFaceConsultationPresent(consultations);
    const incompleteHairConsultation = this.consultationSessionService.findIncompleteHairConsultationPresent(consultations);
    const incompleteConsultation = incompleteFaceConsultation || incompleteHairConsultation;

    if (!incompleteConsultation) {
      return;
    }

    const regimenClass = incompleteFaceConsultation
      ? this.appConfig.Shared.Regimen.Class.FACE
      : this.appConfig.Shared.Regimen.Class.HAIR;

    await this.conn.switchUserToState(
      `v4_${regimenClass.toLowerCase()}:`,
      { consultationSessionId: incompleteConsultation.id }
    );
    await this.router.navigate(['/user'], {
      queryParams: { tab: 'regimen', class: regimenClass }
    });
  }

  private async handleFollowupRedirect(regimenExpiryBannerData: RegimenExpiryBannerData, userId: string): Promise<void> {
    let followUpId = regimenExpiryBannerData?.followUpId;
    try {
      const response = await this.conn.createFollowUp();
      followUpId = response?.followUpId || followUpId;
    } catch (error) {
      this.eventLogger.trackEvent('regimen_expiry_banner_followup_error', {
        userId: userId,
        error: error.message,
      });
    }
    this.router.navigate([`/chatV2/${followUpId}`], {
      queryParams: { type: 'followUp' },
    });
  }

  private trackNoProductsEvent(userId: string, regimenExpiryBannerData: RegimenExpiryBannerData): void {
    this.eventLogger.trackEvent('regimen_expiry_banner_clicked_without_products', {
      userId: userId,
      regimenExpiryBannerData: regimenExpiryBannerData,
    });
  }
}
